import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const TrashSettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operations, setOperations] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [restoreSuccess, setRestoreSuccess] = useState(null);
  const [restoreError, setRestoreError] = useState(null);
  const [activeTab, setActiveTab] = useState('leads');
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  
  const navigate = useNavigate();

  // Function to load deleted operations
  const loadOperations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      const response = await axios.get(`/api/restore?table=${activeTab}`, config);
      console.log('API Response:', response.data);
      setOperations(response.data);
    } catch (err) {
      console.error('Error loading trash:', err);
      setError(err.response?.data?.message || 'Failed to load deleted items');
    } finally {
      setLoading(false);
    }
  };
  
  // Load operations when the tab changes
  useEffect(() => {
    loadOperations();
  }, [activeTab]);
  
  // Debug log for operations
  useEffect(() => {
    console.log('Operations:', operations);
  }, [operations]);
  
  // Check if user is a superuser
  useEffect(() => {
    const checkSuperuserStatus = () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;
        
        const user = JSON.parse(userStr);
        // Only superuser (role_id 1) can permanently delete
        if (user && Number(user.role_id) === 1) {
          console.log('User is a superuser - enabling permanent delete');
          setIsSuperuser(true);
        } else {
          console.log('User is not a superuser - hiding permanent delete');
          setIsSuperuser(false);
        }
      } catch (error) {
        console.error('Error checking superuser status:', error);
        setIsSuperuser(false);
      }
    };
    
    checkSuperuserStatus();
  }, []);
  
  // Function to restore an operation
  const handleRestore = async (operationId) => {
    setRestoring(true);
    setRestoreSuccess(null);
    setRestoreError(null);
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      const response = await axios.post(`/api/restore/${operationId}`, {}, config);
      
      if (response.data.success) {
        setRestoreSuccess(`Successfully restored items. The ${activeTab} list will be updated.`);
        // Remove the restored operation from the list
        setOperations(operations.filter(op => op.operationId !== operationId));
        
        // Reload the list after restoration
        setTimeout(() => {
          loadOperations();
        }, 2000);
      } else {
        setRestoreError('Failed to restore items. Please try again.');
      }
    } catch (err) {
      setRestoreError(err.response?.data?.message || 'Failed to restore items. The operation may have expired.');
    } finally {
      setRestoring(false);
    }
  };
  
  // Open delete confirmation dialog
  const confirmDelete = (operationId) => {
    console.log('Opening confirmation dialog for operation:', operationId);
    setOperationToDelete(operationId);
    setShowDeleteConfirmation(true);
  };
  
  // Cancel delete confirmation
  const cancelDelete = () => {
    setOperationToDelete(null);
    setShowDeleteConfirmation(false);
  };
  
  // Permanently delete an operation
  const handlePermanentDelete = async () => {
    if (!operationToDelete) return;
    
    setDeleting(true);
    setDeleteSuccess(null);
    setDeleteError(null);
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      console.log('Sending delete request for operation:', operationToDelete);
      const response = await axios.delete(`/api/restore/${operationToDelete}`, config);
      
      if (response.data.success) {
        setDeleteSuccess(`Successfully deleted items permanently.`);
        // Remove the deleted operation from the list
        setOperations(operations.filter(op => op.operationId !== operationToDelete));
      } else {
        setDeleteError('Failed to delete items. Please try again.');
      }
    } catch (err) {
      console.error('Error permanently deleting:', err);
      setDeleteError(err.response?.data?.message || 'Failed to delete items. You may not have admin permissions.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirmation(false);
      setOperationToDelete(null);
    }
  };
  
  // Function to format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      // PostgreSQL timestamps from JSON might be in ISO format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (e) {
      console.error('Date formatting error:', e, dateString);
      return 'Unknown date';
    }
  };
  
  // Function to render table name in a nicer format
  const formatTableName = (tableName) => {
    switch (tableName) {
      case 'leads':
        return 'Leads';
      case 'deals':
        return 'Deals';
      case 'tasks':
        return 'Tasks';
      case 'users':
        return 'Users';
      case 'email_campaigns':
        return 'Email Campaigns';
      case 'invoices':
        return 'Invoices';
      case 'calendar_events':
        return 'Calendar Events';
      default:
        return tableName ? tableName.charAt(0).toUpperCase() + tableName.slice(1).replace('_', ' ') : 'Unknown';
    }
  };

  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recently Deleted Items</h2>
        <p className="text-gray-600 mb-4">
          Items deleted within the last 30 days can be restored from here. After 30 days, they are permanently removed.
        </p>
        
        {/* Tabs for different tables */}
        <div className="flex flex-wrap space-x-2 mb-6">
          {['leads', 'deals', 'tasks', 'users', 'email_campaigns', 'invoices', 'calendar_events'].map(tab => (
            <button
              key={tab}
              className={`px-4 py-2 text-sm rounded-md transition-colors mb-2 ${
                activeTab === tab 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {formatTableName(tab)}
            </button>
          ))}
        </div>
      </div>
      
      {/* Loading and error states */}
      {loading && (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading deleted items...</p>
        </div>
      )}
      
      {error && !loading && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {/* Success message */}
      {restoreSuccess && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
          <p className="text-green-700">{restoreSuccess}</p>
        </div>
      )}
      
      {/* Error message for restore */}
      {restoreError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{restoreError}</p>
        </div>
      )}
      
      {/* Success message for permanent delete */}
      {deleteSuccess && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
          <p className="text-green-700">{deleteSuccess}</p>
        </div>
      )}
      
      {/* Error message for permanent delete */}
      {deleteError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{deleteError}</p>
        </div>
      )}
      
      {/* Delete confirmation modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-red-600 mb-4">Confirm Permanent Deletion</h3>
            <p className="mb-6 text-gray-700">
              Are you sure you want to permanently delete these items? This action cannot be undone and the data will be lost forever.
            </p>
            <div className="flex justify-end space-x-4">
              <button 
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={handlePermanentDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="mr-2">Deleting...</span>
                    <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div>
                  </>
                ) : (
                  'Permanently Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* No operations */}
      {!loading && !error && operations.length === 0 && (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <p className="text-lg text-gray-600">No recently deleted {formatTableName(activeTab)} found</p>
          <p className="text-sm text-gray-500 mt-2">
            Items deleted within the last 30 days would appear here
          </p>
        </div>
      )}
      
      {/* Operations list */}
      {!loading && !error && operations.length > 0 && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deleted By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deleted On
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires On
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {operations.map((operation) => (
                <tr key={operation.operationId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {operation.recordCount || 0} {formatTableName(operation.tableName)}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {operation.operationId ? operation.operationId.substring(0, 8) + '...' : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{operation.deletedBy}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(operation.deletedAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(operation.expiresAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRestore(operation.operationId)}
                        disabled={restoring || deleting}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {restoring ? 'Restoring...' : 'Restore'}
                      </button>
                      
                      {isSuperuser && (
                        <button
                          onClick={() => confirmDelete(operation.operationId)}
                          disabled={restoring || deleting}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleting && operationToDelete === operation.operationId ? 'Deleting...' : 'Delete Permanently'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrashSettingsView;